<template>
    <div class="w-[550px] rounded-md border border-gray-200 bg-white p-4">
        <div class="flex gap-2">
            <n-input class="mb-2 w-8/12" size="small" v-model:value="pattern" placeholder="Search" />
            <n-button-group class="w-4/12" size="small">
                <n-button size="small" type="primary" @click="selectAll">Select All </n-button>
                <n-button size="small" type="tertiary" @click="selectNone">Select None </n-button>
            </n-button-group>
        </div>
        <div class="h-80">
            <n-tree
                ref="documentTree"
                filterable
                :data="transformedDocuments"
                cascade
                checkable
                cancelable
                multiple
                :pattern="pattern"
                block-line
                check-strategy="all"
                key-field="selectableId"
                label-field="filename"
                :consistent-menu-width="false"
                :render-tag="renderTag"
                :render-prefix="renderPrefix"
                :render-label="renderLabel"
                :checked-keys="checkedKeys"
                @update:checked-keys="handleCheckedKeysChange"
                :indeterminate-keys="indeterminateKeys"
                @update:indeterminate-keys="handleIndeterminateKeysChange"
                placement="bottom-start"
                :show-irrelevant-nodes="false"
                virtual-scroll
                show-line
                :selectable="true"
                class="h-full"
            >
            </n-tree>
        </div>
    </div>
</template>

<script setup lang="ts">
import { NInput, NTree, NEllipsis, NTag, NButton, NButtonGroup } from 'naive-ui';
import { ref, computed, h, onMounted, watch, nextTick } from 'vue';
import { findDocuments } from '@/api/basecamp.api';
import { fetchDocumentsFolders } from '@/api/documents-folders.api';
import { IDocument, IDocumentFolder } from '@condo/domain';
import { getMimeTypeIcon } from '@/shared';
import { IDocumentTreeNode, transformDataToTree } from '@/shared/document-viewer.helper';

export interface Props {
    mfhId?: number;
    mfhIds?: number[];
    estateId?: number;
    documentIds?: number[];
    folderIds?: number[];
    maxWidth?: string;
    mimeType?: string;
}

export interface Emits {
    (ev: 'update:documentIds', value: number[]): void;
    (ev: 'update:documents', value: IDocument[]): void;
    (ev: 'update:folderIds', value: number[]): void;
}

const selectableDocument = (documentId: number) => `document-${documentId}`;
const selectableFolder = (folderId: number) => `folder-${folderId}`;

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const documentTree = ref<InstanceType<typeof NTree> | null>(null);
const documents = ref<IDocument[]>([]);
const folders = ref<IDocumentFolder[]>([]);
const pattern = ref('');

const checkedKeys = ref<string[]>([]);
const checkedOptions = ref<IDocumentTreeNode[]>([]);

const handleCheckedKeysChange = (keys: string[], options: IDocumentTreeNode[]) => {
    checkedKeys.value = keys;
    checkedOptions.value = options;
};

const indeterminateKeys = ref<string[]>([]);
const handleIndeterminateKeysChange = (keys: string[], options: IDocumentTreeNode[]) => {
    indeterminateKeys.value = keys;
};

const selectedFolders = computed(() => {
    return checkedOptions.value.filter(option => option?.isFolder);
});

const selectedFiles = computed(() => {
    return checkedOptions.value.filter(option => {
        return !option?.isFolder;
    });
});

watch(
    selectedFolders,
    newValue => {
        const selectedFolderIds = newValue.map(folder => folder.folderId).filter(Boolean) as number[];
        emit('update:folderIds', selectedFolderIds);
    },
    {
        deep: true,
    },
);

watch(
    selectedFiles,
    newValue => {
        const selectedDocumentIds = newValue.map(file => file?.documentId).filter(Boolean);
        emit('update:documentIds', selectedDocumentIds);
        emit('update:documents', newValue.map(v => v?.document).filter(d => !!d) as any as IDocument[]);
    },
    {
        deep: true,
    },
);

const selectAll = async () => {
    checkedKeys.value = [
        ...(documents.value ? documents.value.map(doc => selectableDocument(doc.documentId)) : []),
        ...(folders.value ? folders.value.map(folder => selectableFolder(folder.folderId)) : []),
    ];
    await nextTick();
    const checkedData = documentTree.value?.getCheckedData();
    checkedOptions.value = (checkedData?.options as IDocumentTreeNode[]) ?? [];
};

const selectNone = () => {
    checkedOptions.value = [];
    checkedKeys.value = [];
};

const renderPrefix = ({ option }: { option: IDocumentTreeNode }) => {
    if (option.isFolder) {
        return h('i', {
            class: 'fa-regular fa-folder-open yellow',
            style: 'color: darkorange',
        });
    }
    return h('i', { class: getMimeTypeIcon(option.mimeType) });
};

const renderTag = ({ option, handleClose }: { option: IDocumentTreeNode; handleClose: () => void }) => {
    return h(
        NTag,
        {
            closable: true,
            size: 'small',
            round: true,
            onClose: () => {
                handleClose();
            },
        },
        { default: () => option.filename },
    );
};

const renderLabel = ({ option }: { option: IDocumentTreeNode }) => {
    return h(
        NEllipsis,
        {
            style: { maxWidth: '415px' },
        },
        {
            default: () => option.filename,
            tooltip: () => h('div', { style: { textAlign: 'left' } }, [h('i', {}, option.filename)]),
        },
    );
};

const loadData = async () => {
    const [documentList, folderList] = await Promise.all([
        findDocuments({
            query: {
                ...(props.mfhIds
                    ? {
                          mfhIds: props.mfhIds,
                      }
                    : props.mfhId
                      ? {
                            mfhIds: [props.mfhId],
                        }
                      : {}),
                ...(props.estateId
                    ? {
                          estateIds: [props.estateId],
                      }
                    : {}),
                ...(props.mimeType ? { mimeType: props.mimeType } : {}),
            },
            expand: { expand: { document: { select: ['documentId', 'documentType', 'filename', 'link', 'path', 'mimeType', 'folderId', 'uuid'] } } },
        }),
        // Folders can only be fetched for a single mfh or estate, not multiple
        props.mfhIds
            ? Promise.resolve([])
            : fetchDocumentsFolders({
                  mfhId: props.mfhId,
                  estateId: props.estateId,
              }),
    ]);
    documents.value = documentList;
    folders.value = folderList;
};

const transformedDocuments = computed(() => {
    return transformDataToTree(folders.value as IDocumentFolder[], documents.value as IDocument[]);
});

const getFolderIndeterminateKeys = (folder: IDocumentFolder) => {
    if (
        !folder.parentFolderId ||
        props.folderIds?.includes(folder.parentFolderId) ||
        indeterminateKeys.value.includes(selectableFolder(folder.parentFolderId))
    ) {
        return;
    }

    indeterminateKeys.value.push(selectableFolder(folder.parentFolderId));

    const parentFolder = folders.value.find(f => f.folderId === folder.parentFolderId) as IDocumentFolder;
    if (parentFolder) {
        getFolderIndeterminateKeys(parentFolder);
    }
};

const getDocumentIndeterminateKeys = (document: IDocument) => {
    if (!document.folderId || props.folderIds?.includes(document.folderId) || indeterminateKeys.value.includes(selectableFolder(document.folderId))) {
        return;
    }

    indeterminateKeys.value.push(selectableFolder(document.folderId));

    const parentFolder = folders.value.find(f => f.folderId === document.folderId) as IDocumentFolder;
    if (parentFolder) {
        getFolderIndeterminateKeys(parentFolder);
    }
};

onMounted(async () => {
    await loadData();
    checkedKeys.value = [
        ...(props.documentIds ? props.documentIds.map(selectableDocument) : []),
        ...(props.folderIds ? props.folderIds.map(selectableFolder) : []),
    ];
    folders.value.filter(folder => props.folderIds?.includes(folder.folderId)).forEach(folder => getFolderIndeterminateKeys(folder as IDocumentFolder));
    documents.value
        .filter(document => props.documentIds?.includes(document.documentId))
        .forEach(document => getDocumentIndeterminateKeys(document as IDocument));
});
</script>
<style scoped></style>
